import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { trackAB } from "../lib/ab";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { ASSETS } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NOTION_URL = "https://soapy-count-2be.notion.site/Ghost-The-Grid-Privacy-Disappearance-Pack-2478850f966b80769739f46b5fb4fef8";
const MAX_POLLS = 20;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | error
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(res.data.payment_status)) { setState("error"); return; }
      } catch (e) { /* keep polling */ }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  useEffect(() => { if (state === "paid") trackAB("purchase"); }, [state]);

  return (
    <div className="min-h-screen bg-[#050505] noise-overlay flex items-center justify-center px-6 py-20">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 max-w-lg w-full border border-white/10 bg-[#0a0a0a] p-10 lg:p-14 text-center"
      >
        <img src={ASSETS.logo} alt="Ghost The Grid" className="h-14 w-14 object-cover rounded-sm mx-auto mb-8" />

        {state === "checking" && (
          <>
            <Loader2 className="mx-auto text-accent animate-spin mb-6" size={40} />
            <h1 className="font-display font-black uppercase tracking-tight text-3xl mb-3">Verifying payment</h1>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">Securing transmission…</p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-accent mb-6" size={44} />
            <h1 className="font-display font-black uppercase tracking-tight text-4xl mb-4">You're ghosted.</h1>
            <p className="font-body text-white/55 leading-relaxed mb-8">
              Payment confirmed. We've emailed you a link to set your password and open your member dashboard, your 12 nodes, checklists and progress now live in your own account.
            </p>
            <Link
              to="/portal/login"
              data-testid="access-portal-button"
              className="group inline-flex items-center justify-center gap-3 bg-accent text-white font-mono uppercase tracking-[0.15em] text-sm px-8 py-5 hover:bg-white hover:text-black transition-colors duration-300 w-full"
            >
              Go to your dashboard <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <p className="font-body text-white/40 text-sm mt-5">
              Didn't get the email yet? Check spam, or use{" "}
              <Link to="/portal/forgot-password" className="text-accent hover:text-white transition-colors">set / reset password</Link>.
            </p>
            <a
              href={NOTION_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="access-pack-button"
              className="inline-block mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors"
            >
              Or open the original Notion pack →
            </a>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle className="mx-auto text-white/50 mb-6" size={44} />
            <h1 className="font-display font-black uppercase tracking-tight text-3xl mb-4">Couldn't confirm</h1>
            <p className="font-body text-white/55 leading-relaxed mb-8">
              We couldn't verify this payment yet. If you were charged, it will resolve shortly, check your email for the receipt, or contact support.
            </p>
          </>
        )}

        <Link to="/" data-testid="back-home-link" className="inline-block mt-6 font-mono text-xs uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors">
          ← Back to home
        </Link>
      </motion.div>
    </div>
  );
}
